import { useCallback, useEffect, useMemo, useState } from 'react';
import MetaTags from 'react-meta-tags';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { getPropertyList } from '../../Inventory/pages/FacilityInventory/Helpers/Api';
import { setLoading } from '../../../store/actions';

import { PropertySelector } from './Components/PropertySelector';
import { PreMoveoutListTable } from './Components/PreMoveoutListTable';
import { PreMoveoutFormModal } from './Components/PreMoveoutFormModal';
import { PreMoveoutDetailModal } from './Components/PreMoveoutDetailModal';
import { listPreMoveoutInspections } from './Helpers/Api';
import { decodeUser, REPAIR_CATEGORIES } from './Helpers/format';
import './pre-moveout.scss';

/**
 * Staff page: list of pre-moveout inspections for a property/building, with a
 * full-screen create/edit form and a detail modal (vendors, quotes, approvals).
 */
const PreMoveoutInspection = () => {
  const dispatch = useDispatch();
  const [user] = useState(() => decodeUser());
  const readOnly = String(user?.role || '').toLowerCase() === 'auditor';

  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({ property_id: '', building_name: '', status: 'all' });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(REPAIR_CATEGORIES);
  const [listLoading, setListLoading] = useState(false);

  const [form, setForm] = useState({ open: false, mode: 'create', doc: null });
  const [detail, setDetail] = useState({ open: false, id: null });
  const [detailRefresh, setDetailRefresh] = useState(0);

  const currentProperty = useMemo(
    () => properties.find((p) => p._id === filters.property_id) || null,
    [properties, filters.property_id]
  );
  const propertyHasBuildings = useMemo(
    () => (currentProperty?.building || []).some((b) => b && b.buildingName),
    [currentProperty]
  );
  const selectionReady = !!filters.property_id && (!propertyHasBuildings || !!filters.building_name);

  /* ------------------------------------------------ data */
  const fetchProperties = useCallback(async () => {
    if (!user) return;
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(user.role || '', user.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, user]);

  const fetchList = useCallback(async () => {
    if (!selectionReady) {
      setItems([]);
      return;
    }
    setListLoading(true);
    try {
      const { data, meta } = await listPreMoveoutInspections({
        propertyId: filters.property_id,
        buildingName: filters.building_name || undefined,
        status: filters.status && filters.status !== 'all' ? filters.status : undefined,
      });
      setItems(Array.isArray(data) ? data : []);
      if (Array.isArray(meta?.categories) && meta.categories.length) setCategories(meta.categories);
    } catch (err) {
      setItems([]);
      toast.error(err.message || 'Failed to load pre-moveout inspections');
    } finally {
      setListLoading(false);
    }
  }, [selectionReady, filters.property_id, filters.building_name, filters.status]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /* ------------------------------------------------ handlers */
  const openCreate = () => {
    if (!selectionReady) return;
    setForm({ open: true, mode: 'create', doc: null });
  };

  const openEdit = (doc) => setForm({ open: true, mode: 'edit', doc });

  const closeForm = () => setForm((f) => ({ ...f, open: false }));

  const handleSaved = useCallback(
    (doc) => {
      fetchList();
      if (doc?._id) {
        setDetailRefresh((n) => n + 1);
      }
    },
    [fetchList]
  );

  const openDetail = (doc) => setDetail({ open: true, id: doc._id });
  const closeDetail = () => setDetail((d) => ({ ...d, open: false }));

  const handleDeleted = () => {
    closeDetail();
    fetchList();
  };

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Pre-Moveout Inspections</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Building Operations" breadcrumbItem="Pre-Moveout Inspections" />

        <PropertySelector propertyList={properties} filters={filters} onChange={setFilters} isLoading={listLoading}>
          {!readOnly && (
            <Button
              color="primary"
              onClick={openCreate}
              disabled={!selectionReady}
              title={selectionReady ? 'Record a new pre-moveout inspection' : 'Choose a property and building first'}
              style={{ minHeight: 38 }}
            >
              <FeatherIcon icon="plus" size={16} className="me-1" />
              New inspection
            </Button>
          )}
        </PropertySelector>

        <PreMoveoutListTable items={items} loading={listLoading} hasSelection={selectionReady} onRowClick={openDetail} />

        <PreMoveoutFormModal
          isOpen={form.open}
          onClose={closeForm}
          mode={form.mode}
          doc={form.doc}
          propertyId={filters.property_id}
          buildingName={filters.building_name}
          propertyTitle={currentProperty?.title || ''}
          categories={categories}
          onSaved={handleSaved}
        />

        <PreMoveoutDetailModal
          isOpen={detail.open}
          onClose={closeDetail}
          reportId={detail.id}
          refreshToken={detailRefresh}
          onChanged={fetchList}
          onEdit={openEdit}
          onDeleted={handleDeleted}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export { PreMoveoutInspection };
export default PreMoveoutInspection;
